import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Terminal as XTerm } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { KeyRound, Loader2, Power, SquareTerminal } from "lucide-react";
import { canUpdateCases } from "hooks/useCaseSoftware";
import { errorText } from "./CasesView";
import { Notice, PrimaryButton, SecondaryButton } from "./parts";

const api = () => window.electronAPI?.caseDashboard;

const THEME = {
  background: "#111827",
  foreground: "#E5E7EB",
  cursor: "#E07A3A",
  selectionBackground: "#374151",
};

// A shell on the case (admins). It signs in with this computer's SSH key
// first; if the case doesn't take it, it asks for the Pi password, which
// is used for this session only and never stored.
const TerminalView = ({ caseId, caseRecord }) => {
  const { t } = useTranslation();
  const isAdmin = canUpdateCases();
  const boxRef = useRef(null);
  const termRef = useRef(null);
  const fitRef = useRef(null);
  const idRef = useRef(null);
  // connecting | open | closed | password
  const [phase, setPhase] = useState("connecting");
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");

  // One xterm for the life of the tab. React (StrictMode, in development)
  // mounts twice; disposing an xterm that has just opened crashes its first
  // redraw, so the terminal is reused across that and only disposed (with
  // its shell) a moment after the tab really closes.
  const disposeTimer = useRef(null);
  useEffect(() => {
    if (!isAdmin || !boxRef.current) return undefined;
    clearTimeout(disposeTimer.current);
    let term = termRef.current;
    let fit = fitRef.current;
    if (!term) {
      term = new XTerm({
        fontFamily: '"SF Mono", Menlo, Monaco, Consolas, monospace',
        fontSize: 13,
        cursorBlink: true,
        scrollback: 5000,
        theme: THEME,
      });
      fit = new FitAddon();
      term.loadAddon(fit);
      term.open(boxRef.current);
      termRef.current = term;
      fitRef.current = fit;
    }
    const refit = () => {
      // Only when it has a size: fitting a hidden terminal throws.
      if (!boxRef.current?.offsetWidth) return;
      try {
        fit.fit();
      } catch {
        // between tabs
      }
    };
    const frame = requestAnimationFrame(refit);

    const offData = api()?.onTerminalData?.(({ termId, data }) => {
      if (termId === idRef.current) term.write(data);
    });
    const offExit = api()?.onTerminalExit?.(({ termId }) => {
      if (termId !== idRef.current) return;
      idRef.current = null;
      term.write(`\r\n\x1b[90m${t("caseDashboard.terminal.ended")}\x1b[0m\r\n`);
      setPhase("closed");
    });
    const typed = term.onData((data) => {
      if (idRef.current) api().terminalInput(idRef.current, data);
    });
    const resized = term.onResize(({ cols, rows }) => {
      if (idRef.current) api().terminalResize(idRef.current, cols, rows);
    });
    const observer = new ResizeObserver(refit);
    observer.observe(boxRef.current);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      typed.dispose();
      resized.dispose();
      offData?.();
      offExit?.();
      disposeTimer.current = setTimeout(() => {
        if (idRef.current) api()?.terminalClose(idRef.current);
        idRef.current = null;
        termRef.current = null;
        fitRef.current = null;
        term.dispose();
      }, 300);
    };
  }, [isAdmin, t]);

  const open = useCallback(
    async (pw) => {
      const term = termRef.current;
      if (!term) return;
      setError(null);
      setPhase("connecting");
      const res = await api().terminalOpen(caseId, {
        password: pw || undefined,
        cols: term.cols,
        rows: term.rows,
      });
      setPassword("");
      if (!res?.ok) {
        const code = res?.error?.code;
        // Our key wasn't taken (or there isn't one): ask for the password.
        if (!pw && (code === "auth-key" || code === "no-key")) {
          setPhase("password");
          return;
        }
        setError(res?.error || { message: "failed" });
        setPhase(code === "auth" ? "password" : "closed");
        return;
      }
      idRef.current = res.termId;
      term.reset();
      setPhase("open");
      term.focus();
    },
    [caseId]
  );

  // Try this computer's key as soon as the tab opens.
  const tried = useRef(false);
  useEffect(() => {
    if (!isAdmin || tried.current || !termRef.current) return;
    tried.current = true;
    open(null);
  }, [isAdmin, open]);

  const close = () => {
    if (idRef.current) api().terminalClose(idRef.current);
  };

  if (!isAdmin) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-bold text-[#111827]">
          {t("caseDashboard.tabs.terminal")}
        </h1>
        <Notice>{t("caseDashboard.terminal.adminsOnly")}</Notice>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-[480px] flex-col space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-[#111827]">
            {t("caseDashboard.tabs.terminal")}
          </h1>
          <p className="text-[13px] text-[#6B7280]">
            {t("caseDashboard.terminal.intro", {
              user: caseRecord?.username || "sitcase",
              host: caseRecord?.hostname || caseRecord?.host,
            })}
          </p>
        </div>
        {phase === "open" ? (
          <SecondaryButton onClick={close}>
            <Power size={14} />
            {t("caseDashboard.terminal.close")}
          </SecondaryButton>
        ) : phase === "closed" ? (
          <PrimaryButton onClick={() => open(null)}>
            <SquareTerminal size={14} />
            {t("caseDashboard.terminal.open")}
          </PrimaryButton>
        ) : null}
      </div>

      {phase === "password" && (
        <form
          className="flex flex-wrap items-center gap-2 rounded-xl border border-[#F3F4F6] bg-white p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (password) open(password);
          }}
        >
          <KeyRound size={16} className="text-[#9CA3AF]" />
          <label
            htmlFor="case-terminal-password"
            className="text-[13px] text-[#374151]"
          >
            {t("caseDashboard.terminal.passwordLabel")}
          </label>
          <input
            id="case-terminal-password"
            type="password"
            autoComplete="off"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="min-w-[200px] flex-1 rounded-[10px] border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:border-[#E07A3A]"
          />
          <PrimaryButton type="submit" disabled={!password}>
            {t("caseDashboard.terminal.signIn")}
          </PrimaryButton>
          <p className="w-full text-[11px] text-[#9CA3AF]">
            {t("caseDashboard.terminal.passwordNote")}
          </p>
        </form>
      )}

      {error && <Notice tone="error">{errorText(t, error)}</Notice>}

      <div className="relative min-h-0 flex-1 overflow-hidden rounded-xl bg-[#111827] p-2">
        <div ref={boxRef} className="h-full w-full" />
        {phase === "connecting" && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 text-[13px] text-[#9CA3AF]">
            <Loader2 size={16} className="animate-spin" />
            {t("caseDashboard.terminal.connecting")}
          </div>
        )}
      </div>
    </div>
  );
};

export default TerminalView;
